import type {FC} from 'react';
import React from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {NODE_WIDTH} from '../inboxGraphTypes';

/*
 * Type.
 */

export interface RectangleNodeProps {
  height: number;
  defaultFill: string;
  hoveredFill?: string;
  stroke?: string;
  strokeWidth?: number;
  hasShadow?: boolean;
  onClick?: Handler;
  tooltipContent: string;
}

const NODE_CORNER_RADIUS = 12;

/*
 * Style.
 */

const StyledWrapper = styled.div<{
  $defaultFill: string;
  $hoveredFill?: string;
  $stroke?: string;
  $strokeWidth?: number;
  $hasShadow?: boolean;
}>`
  border-radius: ${NODE_CORNER_RADIUS}px;
  background-color: ${(p) => p.$defaultFill};
  ${(p) => p.$hasShadow && boxShadow(p.theme, `0px 1px 9px rgba(0, 0, 0, 0.08)'}`)};
  ${(p) => p.$stroke && `border: ${p.$strokeWidth || 1}px ${p.$stroke} solid;`};

  ${(p) =>
    p.$hoveredFill &&
    `
    &:hover {
      background-color: ${p.$hoveredFill};
    }`}
`;

const StyledNodeSvg = styled.svg<{$isClickable?: boolean}>`
  overflow: visible;
  ${(p) => p.$isClickable && 'cursor: pointer'};
`;

const StyledForeignObject = styled.foreignObject`
  overflow: visible;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  position: static;
  width: 100%;
  padding: 16px 0px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/*
 * Components.
 */

/**
 * Returns an svg rectangle used to render some nodes in an inbox graph.
 */
export const RectangleNodeComponent: FC<RectangleNodeProps> = ({
  height,
  defaultFill,
  hoveredFill,
  children,
  stroke,
  strokeWidth,
  onClick,
  tooltipContent,
  hasShadow,
}) => {
  const [isHovered, toggleIsHovered] = useToggle(false);
  const isClickable = onClick !== undefined;
  const theme = useUserTheme();

  return (
    <StyledNodeSvg
      viewBox={`${NODE_WIDTH / 2} ${height / 2} ${NODE_WIDTH} ${height}`} // Viewbox origin is set so that rectangle get centered where it was created.
      width={NODE_WIDTH}
      height={height}
      className="node"
      onMouseEnter={() => isClickable && toggleIsHovered()}
      onMouseLeave={() => isClickable && toggleIsHovered()}
      onClick={() => onClick?.()}
      $isClickable={isClickable}
    >
      <StyledForeignObject x={0} y={0} height="100%" width="100%">
        <StyledWrapper
          $defaultFill={defaultFill}
          $hoveredFill={hoveredFill}
          $stroke={stroke}
          $strokeWidth={strokeWidth}
          $hasShadow={hasShadow}
        >
          <StyledTooltipCoordinator
            render={() => tooltipContent}
            placement="top"
            className="tooltipContainer"
          >
            {children}
          </StyledTooltipCoordinator>
        </StyledWrapper>
      </StyledForeignObject>
      {/* Need to keep that part in svg because any relative content is invisible when graph is being displayed in modal (due to chrome bug). */}
      {isHovered && (
        <g transform={`translate(${NODE_WIDTH - 24}, 8)`}>
          <Icon size={16} name="externalLinkSmall" color={theme.greys.shade70} />
        </g>
      )}
    </StyledNodeSvg>
  );
};
